import { useEffect, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useRouter } from 'next/router';
import useTopology from '@api/hooks/topology/useTopology';
import { getIsPrimeEnabled } from '@api/selectors/topology';
import CircularLoader from '@base/CircularLoader/CircularLoader';
import useIntersection from '@hooks/useIntersection';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { useIsMobile } from '@stores/AppCommonData';
import pushRoute from '@utils/pushRoute';
import useCardsWithListingLock from '../testTab/useCardsWithListingLock';
import TestSeriesListingTestCard from './TestSeriesListingTestCard';
import TestSeriesListingTestCardSkeleton, {
  TEST_SERIES_LISTING_SKELETON_COUNT,
  TEST_SERIES_LISTING_SKELETON_COUNT_MOBILE
} from './TestSeriesListingTestCardSkeleton';
import TestSeriesListingEmptyState from './TestSeriesListingEmptyState';

const CONTENT_MIN_HEIGHT = 434;

const getContentMinHeight = (isFlush, lockedHeight) => {
  if (isFlush) {
    return '0';
  }

  if (lockedHeight) {
    return `${lockedHeight}px`;
  }

  return `${CONTENT_MIN_HEIGHT}px`;
};

const GridWrapper = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  grid-column: span 18;
  width: ${({ $legacy }) => ($legacy ? '851px' : '100%')};
  max-width: ${({ $legacy }) => ($legacy ? '851px' : '852px')};
  margin-bottom: ${({ $isFlush }) => ($isFlush ? '0' : Spacings.SPACING_6B)};

  ${mediaQuery.uptoMobile} {
    width: 100%;
    grid-column: span 10;
    margin-bottom: ${({ $isFlush }) => ($isFlush ? '0' : Spacings.SPACING_4B)};
  }
`;

const ContentArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  width: 100%;
  min-height: ${({ $isFlush, $minHeight }) =>
    getContentMinHeight($isFlush, $minHeight)};
  transition: opacity 0.15s ease;

  ${({ $isRefreshing }) =>
    $isRefreshing
      ? `
    opacity: 0.55;
    pointer-events: none;
  `
      : ''}
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $fluid }) =>
    $fluid
      ? 'repeat(auto-fill, minmax(273px, 1fr))'
      : 'repeat(auto-fill, 273px)'};
  align-items: start;
  gap: ${Spacings.SPACING_4B};
  width: ${({ $legacy }) => ($legacy ? '852px' : '100%')};
  max-width: ${({ $legacy }) => ($legacy ? '852px' : '100%')};

  ${mediaQuery.uptoMobile} {
    grid-template-columns: 1fr;
    width: 100%;
  }
`;

const StatusMessage = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: ${Fonts.REGULAR};
  color: var(--color-text-secondary);
  text-align: center;

  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_6B} ${Spacings.SPACING_4B};
  }
`;

const ScrollSentinel = styled.div`
  width: 100%;
  height: ${Spacings.SPACING_3B};
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 48px;
`;

const getGoalUidFromQuery = (goalUID) =>
  Array.isArray(goalUID) ? goalUID[0] : goalUID;

const TestSeriesListingTestCardsGrid = ({
  cards,
  isInitialLoading,
  isRefreshing,
  isError,
  hasMore,
  onLoadMore,
  isValidating,
  onCardClick,
  isFlush,
  useFluidColumns = true
}) => {
  const useLegacyListingLayout = !useFluidColumns;
  const { query } = useRouter();
  const goalUid = getGoalUidFromQuery(query.goalUID);
  const { topology } = useTopology(goalUid);
  const isPrimeEnabled = getIsPrimeEnabled(topology);
  const cardsWithLock = useCardsWithListingLock(goalUid, cards);
  const isMobile = useIsMobile();
  const contentRef = useRef(null);
  const stableHeightRef = useRef(null);
  const skeletonCount = isMobile
    ? TEST_SERIES_LISTING_SKELETON_COUNT_MOBILE
    : TEST_SERIES_LISTING_SKELETON_COUNT;
  const { intersecting, ref: scrollSentinelRef } = useIntersection();
  const shouldLoadMore =
    !isInitialLoading &&
    !isError &&
    cardsWithLock.length > 0 &&
    hasMore &&
    !isValidating;

  useEffect(() => {
    if (shouldLoadMore && intersecting && onLoadMore) {
      onLoadMore();
    }
  }, [intersecting, shouldLoadMore, onLoadMore]);

  useLayoutEffect(() => {
    const element = contentRef.current;

    if (!element || isInitialLoading || isRefreshing) {
      return;
    }

    stableHeightRef.current = element.getBoundingClientRect().height;
  }, [cards.length, isError, isInitialLoading, isRefreshing]);

  const lockedMinHeight =
    isRefreshing && stableHeightRef.current ? stableHeightRef.current : null;

  const renderContent = () => {
    if (isInitialLoading) {
      return (
        <CardsGrid
          aria-busy="true"
          $fluid={useFluidColumns}
          $legacy={useLegacyListingLayout}
        >
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <TestSeriesListingTestCardSkeleton
              key={`test-card-skeleton-${index}`}
            />
          ))}
        </CardsGrid>
      );
    }

    if (isError) {
      return (
        <StatusMessage>
          We could not load tests right now. Please try again.
        </StatusMessage>
      );
    }

    if (!cardsWithLock.length) {
      return <TestSeriesListingEmptyState />;
    }

    return (
      <CardsGrid $fluid={useFluidColumns} $legacy={useLegacyListingLayout}>
        {cardsWithLock.map(({ id, href, isLive = false, variant, ...test }) => (
          <TestSeriesListingTestCard
            key={id}
            useFixedWidth={useLegacyListingLayout}
            isPrimeEnabled={isPrimeEnabled}
            variant={variant}
            isLive={isLive}
            href={href}
            onClick={
              href
                ? () =>
                    onCardClick
                      ? onCardClick({ id, href, isLive, variant, ...test })
                      : pushRoute(href)
                : undefined
            }
            categoryLabel={test.categoryLabel}
            title={test.title}
            description={test.description}
            subscriptionType={test.subscriptionType}
            showLock={test.showLock}
            statusTag={test.statusTag}
            questionCount={test.questionCount}
            durationMinutes={test.durationMinutes}
            testCount={test.testCount}
            dateRangeLabel={test.dateRangeLabel}
          />
        ))}
      </CardsGrid>
    );
  };

  return (
    <GridWrapper
      aria-label="Test series list"
      aria-busy={isInitialLoading}
      $isFlush={isFlush}
      $legacy={useLegacyListingLayout}
    >
      <ContentArea
        ref={contentRef}
        $minHeight={lockedMinHeight}
        $isFlush={isFlush}
        $isRefreshing={isRefreshing}
      >
        {renderContent()}
      </ContentArea>
      {hasMore && cardsWithLock.length > 0 ? (
        <ScrollSentinel ref={scrollSentinelRef} aria-hidden />
      ) : null}
      {isValidating && cardsWithLock.length > 0 && hasMore ? (
        <LoaderWrapper aria-busy="true" aria-label="Loading more tests">
          <CircularLoader />
        </LoaderWrapper>
      ) : null}
    </GridWrapper>
  );
};

export default TestSeriesListingTestCardsGrid;

TestSeriesListingTestCardsGrid.propTypes = {
  cards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      variant: PropTypes.oneOf(['quiz', 'test_series']),
      categoryLabel: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      description: PropTypes.string,
      subscriptionType: PropTypes.string.isRequired,
      showLock: PropTypes.bool,
      statusTag: PropTypes.oneOf(['ATTEMPTED', 'CANCELLED']),
      questionCount: PropTypes.number,
      durationMinutes: PropTypes.number,
      testCount: PropTypes.number,
      dateRangeLabel: PropTypes.string,
      isLive: PropTypes.bool,
      href: PropTypes.string
    })
  ),
  isInitialLoading: PropTypes.bool,
  isRefreshing: PropTypes.bool,
  isError: PropTypes.bool,
  hasMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  isValidating: PropTypes.bool,
  onCardClick: PropTypes.func,
  isFlush: PropTypes.bool,
  useFluidColumns: PropTypes.bool
};

TestSeriesListingTestCardsGrid.defaultProps = {
  cards: [],
  isInitialLoading: false,
  isRefreshing: false,
  isError: false,
  hasMore: false,
  onLoadMore: undefined,
  isValidating: false,
  onCardClick: undefined,
  isFlush: false,
  useFluidColumns: true
};
